/**
 * Whether a chart can honestly be drawn from the copy on this phone.
 *
 * These answer "is the raw material here", never "is this role allowed" — the
 * same rule as view.ts, for the same reason. A restricted copy does not contain
 * the ledgers that were withheld, so a chart built on them draws a confident
 * picture of a business with no cash. Graphs is a nav destination of its own, so
 * without these guards it hands back the very screens view.ts took away.
 *
 * Kept out of the screen so it can be tested without a DOM.
 */
import type { Snapshot } from "@tally/shared";

import { hasSales } from "./charts.js";
import type { GroupIndex } from "./groups.js";
import { hasMoneyLedgers } from "../view.js";

export type ChartData = { snapshot: Snapshot; groups: GroupIndex; asOf: string };

/** null when the chart can be drawn; a sentence explaining why not otherwise. */
export type Availability = (ctx: ChartData) => string | null;

/**
 * The wording turns on the CAUSE. A role was handed less than the whole; a full
 * copy that lacks something is telling you about the books themselves, and
 * blaming a "view" there would be nonsense.
 */
const because = (snapshot: Snapshot, restricted: string, plain: string): string =>
  snapshot.role ? `The ${snapshot.role.label} view does not include ${restricted}.` : plain;

export const needsMoneyLedgers: Availability = ({ snapshot }) =>
  hasMoneyLedgers(snapshot)
    ? null
    : because(
        snapshot,
        "the cash and bank ledgers — those figures are not on this phone",
        "These books have no cash or bank ledger to build this from.",
      );

export const needsStock: Availability = ({ snapshot }) =>
  snapshot.stockItems.length > 0
    ? null
    : because(snapshot, "stock", "These books hold no stock items.");

export const needsSales: Availability = ({ snapshot }) =>
  hasSales(snapshot) ? null : because(snapshot, "sale vouchers", "No sales in these books yet.");

/**
 * Guards in order, FIRST reason wins — so pass the broad one first and the
 * specific one last. "There is no stock at all" is a better answer than "too
 * few items sold to draw a curve", which invites the reader to go and sell more.
 */
export const allOf =
  (...guards: Availability[]): Availability =>
  (ctx) => {
    for (const guard of guards) {
      const why = guard(ctx);
      if (why) return why;
    }
    return null;
  };
